import { useEffect, useId, useRef, type ReactNode } from 'react';
import type { CSSProperties } from 'react';
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Segmented,
  Space,
  Spin,
  Switch,
  theme,
  Typography,
} from 'antd';
import type { FormInstance } from 'antd';
import { ClienteSelect } from '../../../components/ClienteSelect';
import { useCliente, useClienteResumen, useClienteServicios } from '../../clientes/api/useClientes';
import { CURRENCY_LOCALE, formatCurrency, getCurrencySymbol } from '../../../lib/format';
import { i18nInstance, useT } from '../../../locales';
import { INVOICE_TYPE } from '@aid/shared';
import type {
  ClienteResponse,
  CobroClienteResponse,
  InvoiceType,
  ServicioResponse,
} from '@aid/shared';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import { parseDateOnly, toDateOnlyString } from '../../../lib/date';

const itemStyle: CSSProperties = { marginBottom: 12 };
const PERIOD_DATE_FORMAT = 'D MMM YYYY';
const amountNumberFormatter = new Intl.NumberFormat(CURRENCY_LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

interface InputNumberFormatInfo {
  userTyping: boolean;
  input: string;
}

function formatMoneyInput(
  value: number | string | undefined,
  info?: InputNumberFormatInfo,
): string {
  if (info?.userTyping) return info.input;
  if (value === undefined || value === '') return '';

  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? amountNumberFormatter.format(parsed) : '';
}

function parseMoneyInput(value: string | undefined): number | string {
  if (!value) return '';

  const parsed = Number(value.replace(/,/g, '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : '';
}

function parseOptionalMoney(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || String(value).trim() === '') return null;

  const parsed = Number(
    String(value)
      .replace(/,/g, '')
      .replace(/[^\d.-]/g, ''),
  );
  return Number.isFinite(parsed) ? Math.max(parsed, 0) : null;
}

function parsePositiveMoney(value: string | number | null | undefined): number | null {
  const parsed = parseOptionalMoney(value);
  return parsed !== null && parsed > 0 ? parsed : null;
}

function parseMoneyValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(value, 0);
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.replace(/,/g, '').replace(/[^\d.-]/g, ''));
    return Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;
  }
  return 0;
}

function toDayjs(value: unknown): Dayjs | undefined {
  if (dayjs.isDayjs(value)) return value;
  if (value instanceof Date || typeof value === 'string') {
    return parseDateOnly(value) ?? undefined;
  }
  return undefined;
}

function formatPeriodicDate(value: Dayjs): string {
  const locale = i18nInstance.language.toLowerCase().startsWith('en') ? 'en' : 'es';
  return value.locale(locale).format(PERIOD_DATE_FORMAT);
}

function nextBillingDate(value: Date | string, diaCobro: number | null): Dayjs {
  const start = (parseDateOnly(value) ?? dayjs(value)).startOf('day');
  const dia = diaCobro ?? start.date();
  const targetMonth = start.add(1, 'month');
  return targetMonth.date(Math.min(dia, targetMonth.daysInMonth()));
}

interface FormSectionProps {
  title: string;
  children: ReactNode;
  noBottomMargin?: boolean;
}

function FormSection({ title, children, noBottomMargin = false }: FormSectionProps) {
  const { token } = theme.useToken();

  return (
    <section aria-label={title} style={{ marginBottom: noBottomMargin ? 0 : token.marginLG }}>
      <Typography.Title
        level={5}
        style={{ margin: 0, marginBottom: token.marginSM, fontSize: 15, lineHeight: 1.3 }}
      >
        {title}
      </Typography.Title>
      {children}
    </section>
  );
}

interface PeriodicDateFieldsProps {
  form: FormInstance;
  t: ReturnType<typeof useT>;
  helpText: string;
}

function PeriodicDateFields({ form, t, helpText }: PeriodicDateFieldsProps) {
  const { token } = theme.useToken();
  const labelId = useId();
  const helperId = useId();
  const fechaInicio = toDayjs(Form.useWatch('fechaInicio', form));
  const fechaFin = toDayjs(Form.useWatch('fechaFin', form));
  const periodLabel = t('pages.cobros.periodoSiguiente');

  return (
    <>
      <Form.Item
        name="fechaInicio"
        hidden
        getValueProps={() => ({ value: '' })}
        rules={[{ required: true, message: t('pages.cobros.fechaInicioRequired') }]}
      >
        <input type="hidden" />
      </Form.Item>
      <Form.Item
        name="fechaFin"
        hidden
        getValueProps={() => ({ value: '' })}
        rules={[{ required: true, message: t('pages.cobros.fechaFinRequired') }]}
      >
        <input type="hidden" />
      </Form.Item>
      <output
        aria-describedby={helperId}
        aria-labelledby={labelId}
        aria-live="polite"
        style={{
          display: 'block',
          marginBottom: token.marginSM,
          minWidth: 0,
        }}
      >
        <span
          id={labelId}
          style={{
            display: 'block',
            color: token.colorText,
            fontWeight: token.fontWeightStrong,
            lineHeight: 1.4,
          }}
        >
          {periodLabel}
        </span>
        <span
          style={{
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: token.marginXS,
            marginTop: token.marginXS,
            color: token.colorText,
            fontSize: token.fontSizeLG,
            fontVariantNumeric: 'tabular-nums',
            fontWeight: token.fontWeightStrong,
            lineHeight: 1.4,
          }}
        >
          {fechaInicio && fechaFin ? (
            <>
              <time dateTime={fechaInicio.format('YYYY-MM-DD')}>
                {formatPeriodicDate(fechaInicio)}
              </time>
              <span aria-hidden="true" style={{ color: token.colorTextSecondary }}>
                →
              </span>
              <time dateTime={fechaFin.format('YYYY-MM-DD')}>{formatPeriodicDate(fechaFin)}</time>
            </>
          ) : (
            <span style={{ color: token.colorTextSecondary }}>{'—'}</span>
          )}
        </span>
        <span
          id={helperId}
          style={{
            display: 'block',
            marginTop: token.marginXS,
            color: token.colorTextSecondary,
            fontSize: token.fontSizeSM,
            lineHeight: 1.5,
          }}
        >
          {helpText}
        </span>
      </output>
    </>
  );
}

function PeriodicDatePickerFields({ t }: { t: ReturnType<typeof useT> }) {
  return (
    <Row gutter={16}>
      <Col xs={24} sm={12}>
        <Form.Item
          style={itemStyle}
          name="fechaInicio"
          label={t('pages.cobros.fechaInicio')}
          rules={[{ required: true, message: t('pages.cobros.fechaInicioRequired') }]}
          extra={t('pages.cobros.periodoSiguienteAyuda')}
        >
          <DatePicker style={{ width: '100%' }} disabled />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item
          style={itemStyle}
          name="fechaFin"
          label={t('pages.cobros.fechaFin')}
          rules={[{ required: true, message: t('pages.cobros.fechaFinRequired') }]}
        >
          <DatePicker style={{ width: '100%' }} disabled />
        </Form.Item>
      </Col>
    </Row>
  );
}

interface PeriodicFieldsProps {
  form: FormInstance;
  t: ReturnType<typeof useT>;
  cliente: ClienteResponse | undefined;
  selectedClienteId: number | undefined;
  monthlyAmount: number | null;
  loading: boolean;
  hasError: boolean;
}

function PeriodicFields({
  form,
  t,
  cliente,
  selectedClienteId,
  monthlyAmount,
  loading,
  hasError,
}: PeriodicFieldsProps) {
  const previousSelectedClienteId = useRef<number | undefined>(selectedClienteId);
  const hasSyncedAmount = useRef(false);

  useEffect(() => {
    if (!selectedClienteId || !cliente?.fechaCobro) {
      form.setFieldsValue({ fechaInicio: undefined, fechaFin: undefined });
      return;
    }

    // The billing cursor is date-only: parse component-wise so a legacy
    // ISO-midnight value cannot shift the period a day back in UTC-negative
    // zones (manual periods must stay the exact billing calendar date).
    const fechaInicio = (parseDateOnly(cliente.fechaCobro) ?? dayjs(cliente.fechaCobro)).startOf(
      'day',
    );
    const fechaFin = nextBillingDate(cliente.fechaCobro, cliente.diaCobro);
    form.setFieldsValue({
      fechaInicio,
      fechaFin,
      fechaCobro: fechaInicio,
    });
  }, [cliente, form, selectedClienteId]);

  useEffect(() => {
    const clientChanged = previousSelectedClienteId.current !== selectedClienteId;
    const firstSync = !hasSyncedAmount.current;
    previousSelectedClienteId.current = selectedClienteId;
    hasSyncedAmount.current = true;
    const shouldResetAmount = firstSync || clientChanged;

    if (shouldResetAmount || !selectedClienteId || monthlyAmount === null) {
      form.resetFields(['valor']);
    }

    if (
      selectedClienteId &&
      monthlyAmount !== null &&
      (shouldResetAmount || !form.isFieldTouched('valor'))
    ) {
      form.setFields([{ name: 'valor', value: monthlyAmount, touched: false }]);
    }
  }, [form, monthlyAmount, selectedClienteId]);

  const helpText = !selectedClienteId
    ? t('pages.cobros.periodoSeleccionaCliente')
    : loading
      ? t('pages.cobros.periodoCargando')
      : hasError
        ? t('pages.cobros.periodoError')
        : cliente?.fechaCobro
          ? t('pages.cobros.periodoSiguienteAyuda')
          : t('pages.cobros.periodoNoDisponible');

  return <PeriodicDateFields form={form} t={t} helpText={helpText} />;
}

const ACTIVE_SERVICE_STATES = new Set(['ACTIVO', 'ACTIVO_AL_CORTE']);
const INCLUDED_SERVICES_LIMIT = 100;

interface IncludedServicesProps {
  t: ReturnType<typeof useT>;
  selectedClienteId: number | undefined;
  monthlyAmount: number | null;
}

function isActiveBillableService(service: ServicioResponse): boolean {
  const stateKey = service.estado?.key?.toUpperCase();
  return (
    service.tipoServicio?.generaCobro === true &&
    stateKey != null &&
    ACTIVE_SERVICE_STATES.has(stateKey)
  );
}

function servicePrice(service: ServicioResponse): number | null {
  if (service.plan?.precio == null) return null;

  const price = Number(service.plan.precio);
  return Number.isFinite(price) ? price : null;
}

function sumServicePrices(services: ServicioResponse[]): number | null {
  let total = 0;
  let hasPrice = false;

  for (const service of services) {
    const price = servicePrice(service);
    if (price === null || price <= 0) continue;
    total += price;
    hasPrice = true;
  }

  return hasPrice ? total : null;
}

function IncludedServices({ t, selectedClienteId, monthlyAmount }: IncludedServicesProps) {
  const { token } = theme.useToken();
  const servicesQuery = useClienteServicios(selectedClienteId ?? 0, {
    limit: INCLUDED_SERVICES_LIMIT,
    offset: 0,
  });
  const services = (servicesQuery.data?.data ?? []).filter(isActiveBillableService);
  const subtotal = sumServicePrices(services);
  const total = monthlyAmount ?? subtotal;
  const hasError = servicesQuery.isError || servicesQuery.error != null;

  let content: ReactNode;
  if (!selectedClienteId) {
    content = (
      <Typography.Text type="secondary">
        {t('pages.cobros.serviciosIncluidosSeleccionaCliente')}
      </Typography.Text>
    );
  } else if (servicesQuery.isLoading) {
    content = (
      <div role="status" aria-live="polite" style={{ color: token.colorTextSecondary }}>
        <Spin size="small" style={{ marginInlineEnd: token.marginXS }} />
        {t('pages.cobros.serviciosIncluidosCargando')}
      </div>
    );
  } else if (hasError) {
    content = (
      <Typography.Text type="secondary" role="alert">
        {t('pages.cobros.serviciosIncluidosError')}
      </Typography.Text>
    );
  } else if (services.length === 0) {
    content = (
      <Typography.Text type="secondary">
        {t('pages.cobros.serviciosIncluidosVacio')}
      </Typography.Text>
    );
  } else {
    content = (
      <>
        <ul
          aria-label={t('pages.cobros.serviciosIncluidos')}
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {services.map((service) => {
            const price = servicePrice(service);
            const serviceType = service.tipoServicio?.nombre ?? `#${service.idTipoServicio}`;
            const plan = service.plan?.nombre ?? `#${service.idPlan}`;

            return (
              <li
                key={service.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: token.marginSM,
                  paddingBlock: token.paddingXS,
                  borderBottom: `1px solid ${token.colorSplit}`,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Typography.Text strong style={{ display: 'block', overflowWrap: 'anywhere' }}>
                    {serviceType}
                  </Typography.Text>
                  <Typography.Text
                    type="secondary"
                    style={{ display: 'block', overflowWrap: 'anywhere' }}
                  >
                    {t('pages.cobros.servicioId', { id: service.id })} · {plan}
                  </Typography.Text>
                </div>
                <div style={{ flexShrink: 0, textAlign: 'end' }}>
                  <Typography.Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {price !== null && price >= 0 ? formatCurrency(price) : '—'}
                  </Typography.Text>
                  <Typography.Text
                    type="secondary"
                    style={{ display: 'block', fontSize: token.fontSizeSM }}
                  >
                    {t('pages.cobros.serviciosIncluidosPorMes')}
                  </Typography.Text>
                </div>
              </li>
            );
          })}
        </ul>
        <div
          style={{
            display: 'grid',
            gap: token.marginXS,
            marginTop: token.marginSM,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: token.marginSM }}>
            <Typography.Text type="secondary">
              {t('pages.cobros.serviciosIncluidosSubtotal')}
            </Typography.Text>
            <Typography.Text style={{ fontVariantNumeric: 'tabular-nums' }}>
              {subtotal === null ? '—' : formatCurrency(subtotal)}
            </Typography.Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: token.marginSM }}>
            <Typography.Text strong>{t('pages.cobros.serviciosIncluidosTotal')}</Typography.Text>
            <Typography.Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
              {total === null ? '—' : formatCurrency(total)}
            </Typography.Text>
          </div>
        </div>
      </>
    );
  }

  return (
    <Collapse
      bordered
      size="small"
      defaultActiveKey={[]}
      destroyOnHidden
      style={{ marginBottom: token.marginSM }}
      items={[
        {
          key: 'included-services',
          label: t('pages.cobros.serviciosIncluidos'),
          children: content,
        },
      ]}
    />
  );
}

interface CreditOptionsProps {
  form: FormInstance;
  t: ReturnType<typeof useT>;
  /**
   * Applicable credit (available ledger lots) from the client SUMMARY.
   * `undefined` while unknown (no client selected, loading or error).
   *
   * It is never the detail's or the statement's net balance: since migration
   * 0064 that net also counts funds reserved in prepaid packages and unbilled
   * payments, so showing it would advertise money the backend cannot apply
   * (invoice creation consumes cliente_credito lots FIFO).
   */
  creditoDisponible: string | undefined;
  selectedClienteId: number | undefined;
  loading: boolean;
  hasError: boolean;
}

function CreditOptions({
  form,
  t,
  creditoDisponible,
  selectedClienteId,
  loading,
  hasError,
}: CreditOptionsProps) {
  const { token } = theme.useToken();
  const aplicarSaldoAFavor = Form.useWatch('aplicarSaldoAFavor', form) !== false;
  const amount = parseMoneyValue(Form.useWatch('valor', form));
  const availableCredit = parseOptionalMoney(creditoDisponible);
  const appliedCredit =
    availableCredit === null || !aplicarSaldoAFavor ? 0 : Math.min(amount, availableCredit);
  const remainingBalance = Math.max(amount - appliedCredit, 0);
  const creditHelper =
    availableCredit !== null
      ? availableCredit > 0
        ? t('pages.cobros.saldoAFavorDisponible', { amount: formatCurrency(availableCredit) })
        : t('pages.cobros.saldoAFavorSinCredito')
      : loading
        ? t('pages.cobros.saldoAFavorCargando')
        : hasError || selectedClienteId != null
          ? t('pages.cobros.saldoAFavorNoDisponible')
          : t('pages.cobros.saldoAFavorSeleccionaCliente');

  return (
    <div
      style={{
        padding: `${token.paddingXS}px ${token.paddingSM}px`,
        background: token.colorFillQuaternary,
        borderRadius: token.borderRadiusSM,
        marginBottom: token.marginSM,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: token.marginSM }}>
        <Form.Item name="aplicarSaldoAFavor" valuePropName="checked" noStyle>
          <Switch aria-label={t('pages.cobros.aplicarSaldoAFavor')} />
        </Form.Item>
        <div style={{ minWidth: 0 }}>
          <Typography.Text strong style={{ display: 'block', lineHeight: 1.4 }}>
            {t('pages.cobros.aplicarSaldoAFavor')}
          </Typography.Text>
          <Typography.Text
            type="secondary"
            style={{ display: 'block', fontSize: 12, lineHeight: 1.5 }}
          >
            {creditHelper}
          </Typography.Text>
        </div>
      </div>
      {availableCredit !== null ? (
        <Typography.Text
          type="secondary"
          aria-live="polite"
          style={{ display: 'block', marginTop: token.marginXS, fontSize: 12, lineHeight: 1.5 }}
        >
          {amount > 0
            ? aplicarSaldoAFavor && appliedCredit > 0
              ? t('pages.cobros.saldoAFavorPreview', {
                  applied: formatCurrency(appliedCredit),
                  remainder: formatCurrency(remainingBalance),
                })
              : t('pages.cobros.saldoAFavorPreviewNo', {
                  remainder: formatCurrency(amount),
                })
            : t('pages.cobros.saldoAFavorMontoRequired')}
        </Typography.Text>
      ) : null}
    </div>
  );
}

interface InvoiceDateAmountFieldsProps {
  mode: 'create' | 'edit';
  t: ReturnType<typeof useT>;
  isExtraordinary: boolean;
}

function InvoiceDateAmountFields({ mode, t, isExtraordinary }: InvoiceDateAmountFieldsProps) {
  return (
    <Row gutter={16}>
      <Col xs={24} sm={12}>
        <Form.Item
          style={itemStyle}
          name="fechaCobro"
          label={t(mode === 'create' ? 'pages.cobros.fechaEmision' : 'pages.cobros.fechaCobro')}
          rules={[{ required: true, message: t('pages.cobros.fechaCobroRequired') }]}
        >
          <DatePicker style={{ width: '100%' }} disabled={mode === 'edit' || !isExtraordinary} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item
          style={itemStyle}
          name="valor"
          label={t('pages.cobros.valor')}
          rules={[{ required: true, message: t('pages.cobros.valorRequired') }]}
        >
          <InputNumber
            min={0}
            step={0.01}
            precision={2}
            prefix={getCurrencySymbol()}
            formatter={formatMoneyInput}
            parser={parseMoneyInput}
            style={{ width: '100%' }}
            disabled={mode === 'edit'}
          />
        </Form.Item>
      </Col>
    </Row>
  );
}

interface CommentFieldProps {
  t: ReturnType<typeof useT>;
  canEditMetadata: boolean;
  noBottomMargin?: boolean;
}

function CommentField({ t, canEditMetadata, noBottomMargin = false }: CommentFieldProps) {
  return (
    <Form.Item
      style={noBottomMargin ? { marginBottom: 0 } : itemStyle}
      name="comentario"
      label={t('pages.cobros.comentarioOpcional')}
    >
      <Input.TextArea
        autoSize={{ minRows: 3, maxRows: 6 }}
        placeholder={t('pages.cobros.comentarioPlaceholder')}
        disabled={!canEditMetadata}
      />
    </Form.Item>
  );
}

interface CreateInvoiceDetailsProps {
  form: FormInstance;
  t: ReturnType<typeof useT>;
  isExtraordinary: boolean;
  selectedClienteId: number | undefined;
  hideSubmit: boolean;
}

function CreateInvoiceDetails({
  form,
  t,
  isExtraordinary,
  selectedClienteId,
  hideSubmit,
}: CreateInvoiceDetailsProps) {
  const clienteQuery = useCliente(selectedClienteId ?? 0);
  const resumenQuery = useClienteResumen(selectedClienteId ?? 0);
  const monthlyAmount =
    resumenQuery.isLoading || resumenQuery.isError || resumenQuery.error != null
      ? null
      : parsePositiveMoney(resumenQuery.data?.serviciosActivosMontoMensual);

  return (
    <>
      <FormSection title={t('pages.cobros.typeSpecificData')}>
        {isExtraordinary ? (
          <Form.Item
            style={itemStyle}
            name="concepto"
            label={t('pages.cobros.concepto')}
            rules={[{ required: true, message: t('pages.cobros.conceptoRequired') }]}
          >
            <Input placeholder={t('pages.cobros.conceptoPlaceholder')} />
          </Form.Item>
        ) : (
          <>
            <PeriodicFields
              form={form}
              t={t}
              cliente={clienteQuery.data}
              selectedClienteId={selectedClienteId}
              monthlyAmount={monthlyAmount}
              loading={clienteQuery.isLoading}
              hasError={clienteQuery.error != null}
            />
            <IncludedServices
              t={t}
              selectedClienteId={selectedClienteId}
              monthlyAmount={monthlyAmount}
            />
          </>
        )}
        <InvoiceDateAmountFields mode="create" t={t} isExtraordinary={isExtraordinary} />
      </FormSection>

      <FormSection title={t('pages.cobros.options')} noBottomMargin={hideSubmit}>
        <CreditOptions
          form={form}
          t={t}
          creditoDisponible={resumenQuery.data?.creditoDisponible}
          selectedClienteId={selectedClienteId}
          loading={resumenQuery.isLoading}
          hasError={resumenQuery.error != null}
        />
        <CommentField t={t} canEditMetadata noBottomMargin={hideSubmit} />
      </FormSection>
    </>
  );
}

interface CobroFormProps {
  mode: 'create' | 'edit';
  initialValues?: CobroClienteResponse | null;
  preselectedClienteId?: number;
  onSubmit: (values: Record<string, unknown>) => void;
  loading?: boolean;
  form?: FormInstance;
  hideSubmit?: boolean;
  allowMetadataEdit?: boolean;
}

export function CobroForm({
  mode,
  initialValues,
  preselectedClienteId,
  onSubmit,
  loading,
  form: externalForm,
  hideSubmit = false,
  allowMetadataEdit = true,
}: CobroFormProps) {
  const t = useT();
  const [internalForm] = Form.useForm();
  const form = externalForm ?? internalForm;
  const canEditMetadata = mode === 'create' || allowMetadataEdit;
  const selectedClienteId =
    mode === 'create' ? (Form.useWatch('idCliente', form) as number | undefined) : undefined;
  const watchedType =
    mode === 'create' ? (Form.useWatch('tipoFactura', form) as InvoiceType | undefined) : undefined;
  const tipoFactura: InvoiceType =
    watchedType ?? initialValues?.tipoFactura ?? INVOICE_TYPE.PERIODICA;
  const isExtraordinary = tipoFactura === INVOICE_TYPE.EXTRAORDINARIA;
  const previousClienteId = useRef<number | undefined>(undefined);
  const previousType = useRef<InvoiceType | undefined>(undefined);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        idCliente: initialValues.idCliente,
        tipoFactura: initialValues.tipoFactura ?? INVOICE_TYPE.PERIODICA,
        // Date-only contract: parse period dates component-wise (never as
        // UTC instants) so edits keep the exact billing calendar date.
        fechaInicio: parseDateOnly(initialValues.fechaInicio) ?? undefined,
        fechaFin: parseDateOnly(initialValues.fechaFin) ?? undefined,
        fechaCobro: parseDateOnly(initialValues.fechaCobro) ?? undefined,
        valor: initialValues.valor ? Number(initialValues.valor) : undefined,
        aplicarSaldoAFavor: initialValues.aplicarSaldoAFavor ?? true,
        concepto: initialValues.concepto ?? '',
        comentario: initialValues.comentario ?? '',
      });
    } else if (mode === 'create') {
      form.resetFields();
      form.setFieldsValue({
        tipoFactura: INVOICE_TYPE.PERIODICA,
        aplicarSaldoAFavor: true,
        fechaCobro: dayjs(),
      });
      if (preselectedClienteId != null) form.setFieldValue('idCliente', preselectedClienteId);
    }
  }, [initialValues, mode, preselectedClienteId, form]);

  useEffect(() => {
    if (mode !== 'create') {
      previousClienteId.current = undefined;
      return;
    }

    if (
      previousClienteId.current !== undefined &&
      previousClienteId.current !== selectedClienteId
    ) {
      form.resetFields(['concepto']);
    }
    previousClienteId.current = selectedClienteId;
  }, [form, mode, selectedClienteId]);

  useEffect(() => {
    if (mode !== 'create') {
      previousType.current = undefined;
      return;
    }

    if (previousType.current !== undefined && previousType.current !== tipoFactura) {
      form.resetFields(
        isExtraordinary ? ['fechaInicio', 'fechaFin', 'concepto', 'valor'] : ['concepto'],
      );
      form.setFieldValue('fechaCobro', isExtraordinary ? dayjs() : undefined);
    }
    previousType.current = tipoFactura;
  }, [form, isExtraordinary, mode, tipoFactura]);

  const handleFinish = (values: Record<string, unknown>) => {
    if (!canEditMetadata) return;

    const payload: Record<string, unknown> =
      mode === 'edit' ? { comentario: values.comentario } : { ...values };
    if (mode === 'create') {
      payload.tipoFactura = tipoFactura;
      payload.aplicarSaldoAFavor = payload.aplicarSaldoAFavor !== false;
      if (isExtraordinary) {
        payload.fechaInicio = null;
        payload.fechaFin = null;
      } else if (payload.fechaInicio) {
        payload.fechaInicio = toDateOnlyString(payload.fechaInicio as Dayjs);
      }
      if (!isExtraordinary && payload.fechaFin) {
        payload.fechaFin = toDateOnlyString(payload.fechaFin as Dayjs);
      }
      if (payload.fechaCobro) {
        payload.fechaCobro = toDateOnlyString(payload.fechaCobro as Dayjs);
      }
      if (payload.valor !== undefined) payload.valor = Number(payload.valor).toFixed(2);
    }
    onSubmit(payload);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      style={{ maxWidth: 600, width: '100%' }}
    >
      {mode === 'create' ? (
        <FormSection title={t('pages.cobros.invoiceContext')}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                style={itemStyle}
                name="idCliente"
                label={t('pages.cobros.cliente')}
                rules={[{ required: true, message: t('pages.cobros.clienteRequired') }]}
              >
                <ClienteSelect
                  disabled={preselectedClienteId != null}
                  allowClear={preselectedClienteId == null}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                style={itemStyle}
                name="tipoFactura"
                label={t('pages.cobros.tipoFactura')}
                rules={[{ required: true, message: t('pages.cobros.tipoFacturaRequired') }]}
              >
                <Segmented
                  block
                  aria-label={t('pages.cobros.tipoFactura')}
                  options={[
                    {
                      value: INVOICE_TYPE.PERIODICA,
                      label: t('pages.cobros.tipoFacturaPeriodica'),
                    },
                    {
                      value: INVOICE_TYPE.EXTRAORDINARIA,
                      label: t('pages.cobros.tipoFacturaExtraordinaria'),
                    },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </FormSection>
      ) : null}

      {mode === 'create' ? (
        <CreateInvoiceDetails
          form={form}
          t={t}
          isExtraordinary={isExtraordinary}
          selectedClienteId={selectedClienteId}
          hideSubmit={hideSubmit}
        />
      ) : (
        <>
          <FormSection title={t('pages.cobros.typeSpecificData')}>
            {!isExtraordinary ? <PeriodicDatePickerFields t={t} /> : null}
            <InvoiceDateAmountFields mode="edit" t={t} isExtraordinary={isExtraordinary} />
          </FormSection>

          <FormSection title={t('pages.cobros.options')} noBottomMargin={hideSubmit}>
            <CommentField t={t} canEditMetadata={canEditMetadata} noBottomMargin={hideSubmit} />
          </FormSection>
        </>
      )}

      {!hideSubmit ? (
        <Form.Item style={{ marginBottom: 0 }}>
          <Space wrap size={[8, 8]}>
            <Button type="primary" htmlType="submit" loading={loading} disabled={!canEditMetadata}>
              {mode === 'create' ? t('pages.cobros.create') : t('common.saveChanges')}
            </Button>
          </Space>
        </Form.Item>
      ) : null}
    </Form>
  );
}
